import React, { useState } from 'react'
import { Checkbox, InputNumber, Select } from 'antd'
import CustomModal from 'components/global/CustomModal'
import CustomAvatar from 'components/global/CustomAvatar'
import { LeaguePlayerParams } from 'types/league/leagueManage'
import { LeagueTeamResp } from 'types/team/leagueTeam'
import { DivisonListingResp } from 'types/league/division'

interface Props {
  open: boolean
  onClose: () => void
  initialParams: LeaguePlayerParams
  teams: LeagueTeamResp[]
  divisions?: DivisonListingResp[]
  onApply: (params: LeaguePlayerParams) => void
}

const GenderCard: React.FC<{ label: string; selected: boolean; onClick: () => void }> = ({
  label, selected, onClick,
}) => (
  <div
    className="flex align-center justify-center pointer radius-12 py-15px"
    style={{
      flex: 1,
      border: selected ? '2px solid #E69741' : '1px solid #EBEDF1',
      background: selected ? '#FFF8F0' : '#fff',
    }}
    onClick={onClick}
  >
    <p className="text-14 w-500">{label}</p>
  </div>
)

const Divider = () => (
  <div style={{ height: 1, background: '#EBEDF1', margin: '20px 0' }} />
)

const ManagePlayersFilter: React.FC<Props> = ({
  open, onClose, initialParams, teams, divisions = [], onApply,
}) => {
  const [gender, setGender] = useState<'male' | 'female' | ''>(initialParams.gender || '')
  const [minAge, setMinAge] = useState<number | null>(initialParams.minAge ?? null)
  const [maxAge, setMaxAge] = useState<number | null>(initialParams.maxAge ?? null)
  const [teamIds, setTeamIds] = useState<string[]>(initialParams.teamIds || [])
  const [divisionIds, setDivisionIds] = useState<string[]>(initialParams.divisionIds || [])
  const [sortBy, setSortBy] = useState<LeaguePlayerParams['sortBy']>(initialParams.sortBy)
  const [sortType, setSortType] = useState<'asc' | 'des'>(initialParams.sortType || 'asc')

  const handleClear = () => {
    setGender('')
    setMinAge(null)
    setMaxAge(null)
    setTeamIds([])
    setDivisionIds([])
    setSortBy(undefined)
    setSortType('asc')
  }

  const handleApply = () => {
    const params: LeaguePlayerParams = {}
    if (gender) params.gender = gender
    if (minAge != null) params.minAge = minAge
    if (maxAge != null) params.maxAge = maxAge
    if (teamIds.length) params.teamIds = teamIds
    if (divisionIds.length) params.divisionIds = divisionIds
    if (sortBy) { params.sortBy = sortBy; params.sortType = sortType }
    onApply(params)
    onClose()
  }

  const toggleTeam = (id: string) =>
    setTeamIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id])

  const toggleDivision = (id: string) =>
    setDivisionIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id])

  const toggleGender = (g: 'male' | 'female') =>
    setGender(prev => prev === g ? '' : g)

  return (
    <CustomModal
      openModal={open}
      isCloseModalCustom
      closeModal={onClose}
      title="Filters"
      footer={
        <div
          className="flex align-center justify-between"
          style={{ borderTop: '1px solid #EBEDF1', padding: '16px 20px 4px' }}
        >
          <span
            className="text-14 pointer"
            style={{ textDecoration: 'underline', color: '#908F98' }}
            onClick={handleClear}
          >
            Clear all
          </span>
          <button
            onClick={handleApply}
            style={{
              background: '#2BA7CA',
              borderRadius: 10,
              padding: '10px 28px',
              color: '#fff',
              border: 'none',
              fontSize: 15,
              fontWeight: 500,
              cursor: 'pointer',
            }}
          >
            Show players
          </button>
        </div>
      }
    >
      {/* Sort */}
      <p className="text-16 w-600 mb-12px">Sort by</p>
      <div className="flex gap-10px">
        <div style={{ flex: 1 }}>
          <p className="text-12 w-600 mb-5px">Field</p>
          <Select
            className="w-full"
            placeholder="Select field"
            allowClear
            value={sortBy}
            onChange={v => setSortBy(v)}
            options={[
              { label: 'First Name', value: 'firstName' },
              { label: 'Last Name', value: 'lastName' },
              { label: 'Age', value: 'age' },
            ]}
          />
        </div>
        <div style={{ flex: 1 }}>
          <p className="text-12 w-600 mb-5px">Direction</p>
          <Select
            className="w-full"
            value={sortType}
            onChange={v => setSortType(v)}
            disabled={!sortBy}
            options={[
              { label: 'A → Z / Youngest', value: 'asc' },
              { label: 'Z → A / Oldest', value: 'des' },
            ]}
          />
        </div>
      </div>

      <Divider />

      {/* Age */}
      <p className="text-16 w-600 mb-12px">Age</p>
      <div className="flex gap-15px">
        <div style={{ flex: 1 }}>
          <p className="text-12 w-600 mb-5px">Minimum</p>
          <InputNumber
            min={1} max={100} placeholder="e.g. 5"
            className="br-12 w-full"
            value={minAge}
            onChange={v => setMinAge(v)}
            onWheel={e => e.currentTarget.blur()}
          />
        </div>
        <div style={{ flex: 1 }}>
          <p className="text-12 w-600 mb-5px">Maximum</p>
          <InputNumber
            min={1} max={100} placeholder="e.g. 12"
            className="br-12 w-full"
            value={maxAge}
            onChange={v => setMaxAge(v)}
            onWheel={e => e.currentTarget.blur()}
          />
        </div>
      </div>

      <Divider />

      {/* Gender */}
      <p className="text-16 w-600 mb-12px">Gender</p>
      <div className="flex gap-15px">
        <GenderCard label="Male" selected={gender === 'male'} onClick={() => toggleGender('male')} />
        <GenderCard label="Female" selected={gender === 'female'} onClick={() => toggleGender('female')} />
      </div>

      {/* Divisions */}
      {divisions.length > 0 && (
        <>
          <Divider />
          <p className="text-16 w-600 mb-12px">Division</p>
          <div className="flex flex-column gap-10px">
            {divisions.map(d => (
              <div
                key={d._id}
                className="flex align-center justify-between pointer py-5px"
                onClick={() => toggleDivision(d._id)}
              >
                <p className="text-14">{d.name || `Division (${d.genders?.join('/')})`}</p>
                <Checkbox checked={divisionIds.includes(d._id)} />
              </div>
            ))}
          </div>
        </>
      )}

      {/* Teams */}
      {teams.length > 0 && (
        <>
          <Divider />
          <p className="text-16 w-600 mb-12px">Team</p>
          <div className="flex flex-column gap-10px">
            {teams.map(t => (
              <div
                key={t._id}
                className="flex align-center justify-between pointer py-5px"
                onClick={() => toggleTeam(t._id)}
              >
                <div className="flex align-center gap-10px">
                  <CustomAvatar size={32} src={t.team?.profilePhoto?.thumb} name={t.team?.name} />
                  <p className="text-14">{t.team?.name}</p>
                </div>
                <Checkbox checked={teamIds.includes(t._id)} />
              </div>
            ))}
          </div>
        </>
      )}
    </CustomModal>
  )
}

export default ManagePlayersFilter
